import { useEffect, useState } from "react";

/**
 * Dropdown of known values with a "Custom…" option that swaps to a
 * text input. Used on the Replace VNET form so operators pick from the
 * actually-discovered VNET / bandwidth / shaper / port / display_id
 * values without giving up the ability to type a name the platform
 * hasn't seen yet.
 *
 * When the externally-supplied ``value`` doesn't match any option,
 * the component starts in custom-input mode — so a parent restoring
 * state from URL or local storage doesn't have to know which mode it
 * was last in.
 */
export type Props = {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
  customLabel?: string;
  /** Tailwind classes for the input/select element. */
  className?: string;
  disabled?: boolean;
  /** Optional display text per option (the value stays the raw option). */
  optionLabel?: (option: string) => string;
};

export function SelectOrCustom({
  value,
  onChange,
  options,
  placeholder,
  customLabel = "Custom…",
  className = "w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 font-mono",
  disabled,
  optionLabel,
}: Props) {
  const valueIsKnown = options.includes(value);
  const [mode, setMode] = useState<"select" | "custom">(
    value === "" || valueIsKnown ? "select" : "custom",
  );

  // When options arrive later (async fetch) and the saved value is
  // actually in the list, snap back to select mode.
  useEffect(() => {
    if (mode === "custom" && value !== "" && options.includes(value)) {
      setMode("select");
    }
  }, [mode, value, options]);

  if (mode === "custom") {
    return (
      <div className="flex gap-1">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={className}
        />
        <button
          type="button"
          onClick={() => {
            // Snap back to the dropdown. Clearing the value avoids
            // showing the freshly-typed custom string as a stale
            // selection on the closed dropdown.
            onChange("");
            setMode("select");
          }}
          disabled={disabled}
          className="rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800"
          title="back to dropdown"
        >
          ↩
        </button>
      </div>
    );
  }

  return (
    <select
      value={valueIsKnown ? value : ""}
      onChange={(e) => {
        if (e.target.value === "__custom__") {
          setMode("custom");
          onChange("");
        } else {
          onChange(e.target.value);
        }
      }}
      disabled={disabled}
      className={className}
    >
      <option value="">{placeholder ?? "Select…"}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {optionLabel ? optionLabel(o) : o}
        </option>
      ))}
      <option value="__custom__">{customLabel}</option>
    </select>
  );
}
